import { useCallback, useMemo, useState } from "react";

import { pollAverage } from "./model";
import { POLLS_AS_OF, RACE_POLLS } from "./polls";
import { SILVER_SOURCE } from "./silver";
import {
  loadActiveSource,
  loadSources,
  saveActiveSource,
  saveSources,
} from "./storage";
import type { ExternalSource, Layer } from "./types";
import useLiveResults, { RESULTS_SOURCE_ID } from "./useLiveResults";

/* ── Types ─────────────────────────────────────── */

export type ExternalSources = ReturnType<typeof useExternalSources>;

/* ── Constants ─────────────────────────────────── */

/** The public polls' race averages, margins only. */
const POLLS_SOURCE: ExternalSource = {
  builtIn: true,
  id: "builtin-public-polls",
  importedAt: `${POLLS_AS_OF}T12:00:00`,
  label: "Public polls",
  rows: RACE_POLLS.flatMap(({ office, polls, state }) => {
    const average = pollAverage(polls);
    return average
      ? [{ leftProbability: null, margin: average.margin, office, state }]
      : [];
  }),
};

/* ── Hook ──────────────────────────────────────── */

/**
 * Published forecasts and live results to compare Foresight with (admin
 * view): Silver Bulletin, the live results and the public polls built in
 * plus the sources pasted in this browser, the one compared, and the map
 * layer read from it. The results are compared by default.
 */
const useExternalSources = () => {
  const results = useLiveResults();
  const [activeSourceId, setActiveSourceId] = useState<string | null>(() =>
    loadActiveSource(RESULTS_SOURCE_ID)
  );
  const [layer, setLayer] = useState<Layer>("foresight");
  const [sources, setSources] = useState<ExternalSource[]>(loadSources);

  const allSources = useMemo(
    () => [
      SILVER_SOURCE,
      ...(results.source ? [results.source] : []),
      POLLS_SOURCE,
      ...sources,
    ],
    [results.source, sources]
  );
  const activeSource = allSources.find((s) => s.id === activeSourceId) ?? null;
  // Results have no ensemble: a blend of a forecast and a count means nothing.
  const activeLayer: Layer =
    !activeSource || (activeSource.results && layer === "ensemble")
      ? "foresight"
      : layer;

  const activateSource = useCallback((id: string | null) => {
    setActiveSourceId(id);
    saveActiveSource(id);
    if (!id) setLayer("foresight");
  }, []);

  // Saving under an existing name merges into that source (newer rows win),
  // so a forecaster's Senate and governor tables can be pasted separately.
  const saveSource = useCallback(
    (source: ExternalSource) => {
      const key = source.label.trim().toLowerCase();
      const existing = sources.find(
        (s) => s.label.trim().toLowerCase() === key
      );
      const merged: ExternalSource = existing
        ? {
            ...existing,
            importedAt: source.importedAt,
            rows: [
              ...existing.rows.filter(
                (row) =>
                  !source.rows.some(
                    (r) =>
                      r.office === row.office &&
                      r.state === row.state &&
                      r.district === row.district
                  )
              ),
              ...source.rows,
            ],
          }
        : source;
      const next = existing
        ? sources.map((s) => (s.id === existing.id ? merged : s))
        : [...sources, merged];
      setSources(next);
      saveSources(next);
      activateSource(merged.id);
    },
    [activateSource, sources]
  );

  const deleteSource = useCallback(
    (id: string) => {
      setSources((list) => {
        const next = list.filter((s) => s.id !== id);
        saveSources(next);
        return next;
      });
      if (id === activeSourceId) activateSource(null);
    },
    [activateSource, activeSourceId]
  );

  const layerOptions: { label: string; value: Layer }[] = activeSource?.results
    ? [
        { label: "Our calls", value: "foresight" },
        { label: activeSource.label, value: "external" },
        { label: "Gap", value: "difference" },
      ]
    : [
        { label: "Foresight", value: "foresight" },
        ...(activeSource
          ? [
              { label: activeSource.label, value: "external" as const },
              { label: "Ensemble", value: "ensemble" as const },
              { label: "Gap", value: "difference" as const },
            ]
          : []),
      ];

  return {
    activateSource,
    activeLayer,
    activeSource,
    activeSourceId,
    allSources,
    deleteSource,
    layerOptions,
    resultsError: results.error,
    saveSource,
    setLayer,
  };
};

export default useExternalSources;
